import { App } from "antd";
import type { ArgsProps, MessageType } from "antd/es/message/interface";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import ErrorIcon from "./assets/error-icon.svg";

const DEFAULT_DURATION = 3;

const renderErrorIcon = () => <ErrorIcon className="w-4 h-4 mr-2" />;

export type ToastOptions = Omit<ArgsProps, "type" | "content" | "icon">;

export type ToastHandler = (
  content: ReactNode,
  options?: ToastOptions
) => MessageType;

export function useToast(defaultOptions: ToastOptions = {}) {
  const { message } = App.useApp();

  const open = useCallback(
    (config: ArgsProps): MessageType =>
      message.open({
        duration: DEFAULT_DURATION,
        ...defaultOptions,
        ...config,
      }),
    [defaultOptions, message]
  );

  const success = useCallback<ToastHandler>(
    (content, options) =>
      open({
        type: "success",
        content,
        ...options,
      }),
    [open]
  );

  const error = useCallback<ToastHandler>(
    (content, options) =>
      open({
        type: "error",
        content,
        icon: renderErrorIcon(),
        ...options,
      }),
    [open]
  );

  return useMemo(
    () => ({
      open,
      error,
      success,
    }),
    [error, open, success]
  );
}
